import { PropertyValues } from '@lit/reactive-element';
import { CSSResultGroup } from '@lit/reactive-element/css-tag';

import { html, HTMLTemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { styles } from './button.styles.js';
import { UmToggleButton } from './toggle-button.js';

import '../ripple/ripple.js';

export type UmButtonVariant = 'filled' | 'tonal' | 'elevated' | 'outlined' | 'text';
export type UmButtonColor = 'primary' | 'secondary' | 'tertiary' | 'error';

@customElement('u-button')
export class UmButton extends UmToggleButton {
  static override styles: CSSResultGroup = [UmToggleButton.styles, styles];

  /**
   * The Button variant to render
   */
  @property() variant: UmButtonVariant = 'filled';

  /**
   * The Button color
   */
  @property() color: UmButtonColor = 'primary';

  @property({ type: Boolean, attribute: 'trailing-icon', reflect: true }) trailingIcon = false;

  @state() private _hasIcon = false;
  @state() private _hasSelectionLabel = false;
  @state() animateTextChange = false;

  @query('.label-container', true) private readonly _textWrapper!: HTMLElement;
  @query('#label', true) private readonly _text!: HTMLElement;

  #textSizeObserver!: ResizeObserver | null;

  override firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);

    this.#textSizeObserver = new ResizeObserver(() => this.#setTextWrapperWidth());
    this.#textSizeObserver.observe(this._text);
    this.#setTextWrapperWidth();
  }

  #setTextWrapperWidth(): void {

    const width = `${this._text.offsetWidth}px`;
    this._textWrapper.style.width = width;

    const animateTextChange = !!parseInt(width, 10);

    if (this.animateTextChange === animateTextChange) {
      return;
    }

    setTimeout(() =>
      this.animateTextChange = animateTextChange);
  }

  protected override _getContainerClasses(): Record<string, boolean> {
    return {
      ...super._getContainerClasses(),
      [this.color]: true,
      [this.variant]: true,
      'trailing-icon': this.trailingIcon,
      'has-icon': this._hasIcon,
      'has-selection-label': this._hasSelectionLabel,
    };
  }

  protected override _renderContent(): HTMLTemplateResult {
    const labelContainerClasses = { animate: this.animateTextChange };

    return html`
      <span class="icon-container" aria-hidden="true">
        <span class="icon icon-default">
          <slot name="icon" @slotchange="${this.#handleIconSlotChange}"></slot>
        </span>
        <span class="icon icon-selected">
          <slot name="icon-selected" @slotchange="${this._handleSelectedIconSlotChange}"></slot>
        </span>
      </span>

      <span class="label-container ${classMap(labelContainerClasses)}">
        <span id="label">
          <span class="label label-default">
            <slot></slot>
          </span>
          <span class="label label-selected">
            <slot name="label-selected" @slotchange="${this.#handleSelectedLabelSlotChange}"></slot>
          </span>
        </span>
      </span>
    `;
  }

  #handleIconSlotChange(e: Event) {
    this._hasIcon = (e.target as HTMLSlotElement).assignedElements({ flatten: true }).length > 0;
  }

  #handleSelectedLabelSlotChange(e: Event): void {
    this._hasSelectionLabel = (e.target as HTMLSlotElement).assignedElements({ flatten: true }).length > 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'u-button': UmButton;
  }
}
